import { Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ConsentBanner } from "@/components/ConsentBanner";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { routeConfigs } from "@/config/routes";

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const RootLayout = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ScrollToTop />
        <Toaster />
        <Sonner />
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
        <ConsentBanner />
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

// Generiere Router-Konfiguration aus zentraler Route-Config
const generateRoutes = () => {
  return routeConfigs.map((route) => {
    const Component = route.component;
    
    // Geschützte Routen mit ProtectedRoute wrappen
    if (route.isProtected) {
      return {
        path: route.path,
        errorElement: <RouteErrorBoundary />,
        element: (
          <ProtectedRoute requireAdmin={route.requireAdmin}>
            <Component />
          </ProtectedRoute>
        ),
      };
    }
    
    return {
      path: route.path,
      errorElement: <RouteErrorBoundary />,
      element: <Component />,
    };
  });
};

// Router configuration
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: generateRoutes(),
  },
]);

const App = () => <RouterProvider router={router} />;

export default App;
